<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/scss/index.scss';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';

export default {
  name: 'TableFilter',
  props: {
    filterData: Object
  },
  components: {
    DatePicker,
    VueSlider
  },

  data() {
    return {
      value: null,
      selectedValue: '',
      selectedDate: null,
      isDropdownOpen: true,
      isValidDateRange: false,
      shouldCloseDropdown: false,
      numberRangeValues: [1, 1000]
    };
  },

  filters: {
    rangeFormat(value) {
      if (!value || !value.length) {
        return;
      }

      const [min, max] = value;
      return `$${min} - $${max}`;
    }
  },

  methods: {
    setFilter(value) {
      this.$emit('setFilter', value);
    },

    handleStringSelection(value) {
      const filterString = `${value ? '%' : ''}${value}`.trim();
      this.setFilter(filterString);
    },

    handleEnumSelection(entry) {
      this.selectedValue = entry.label;
      this.setFilter(entry.value);
    },

    handleRangeSelection() {
      this.selectedValue = this.numberRangeValues;
      const filterString = `$num-gte:${this.numberRangeValues[0]}&&$num-lte:${this.numberRangeValues[1]}`;
      this.setFilter(filterString);
    },

    handleDateSelection(dates) {
      const fromDate = dates[0];
      const toDate = dates[1];
      this.selectedValue = `${this.$options.filters.dateLocalized(fromDate)} - ${this.$options.filters.dateLocalized(toDate)}`;
      const filterString = `$date-gte:${dates[0].toISOString()}&&$date-lte:${dates[1].toISOString()}`;
      this.setFilter(filterString);
    },

    handleDatePickerClick() {
      const datePicker = this.$refs.datePicker;
      if (!datePicker) {
        return;
      }

      const activeDatesSelector = '.mx-table-date .active';
      const activeElements = datePicker.$el.querySelectorAll(activeDatesSelector);
      const isValidRange = activeElements.length === 2;
      this.isValidDateRange = isValidRange;
    },

    clearDatePicker() {
      this.selectedValue = null;
      this.selectedDate = null;
      this.isValidDateRange = false;
      this.isDropdownOpen = false;
    }
  }
};
</script>

<template>
  <div>
    <div v-if="filterData.type === 'string'" class="filter filter-input">
      <label class="label">
        <span>{{ filterData.label }}</span>
        <span>{{ filterData.defaultValue }}</span>
      </label>
      <input @input="handleStringSelection($event.target.value)" type="text" spellcheck="false" class="input" />
      <span @click="$emit('remove')" class="remove-button">&times;</span>
    </div>
    <div v-if="filterData.type === 'enum'" class="filter">
      <app-dropdown
        @remove="$emit('remove')"
        @toggle="isDropdownOpen = $event"
        :label="filterData.label"
        :default-value="filterData.defaultValue"
        :selected-value="selectedValue"
        :open="isDropdownOpen"
        hasRemoveButton
      >
        <!-- use entry.label for key, as value can be an array -->
        <div
          @click="
            handleEnumSelection(entry);
            isDropdownOpen = false;
          "
          v-for="entry in filterData.values"
          :key="entry.label"
          class="option"
        >
          {{ entry.label }}
        </div>
      </app-dropdown>
    </div>

    <div v-if="filterData.type === 'dateRange'" class="filter">
      <app-dropdown
        @remove="$emit('remove')"
        @toggle="isDropdownOpen = $event"
        :label="filterData.label"
        :default-value="filterData.defaultValue"
        :selectedValue="selectedValue"
        :open="isDropdownOpen"
        hasRemoveButton
      >
        <date-picker
          @click.native="handleDatePickerClick"
          @confirm="isDropdownOpen = false"
          @change="handleDateSelection"
          v-model="selectedDate"
          ref="datePicker"
          :confirm="true"
          confirm-text="Apply"
          value-type="date"
          :inline="true"
          :open="isDropdownOpen"
          :class="[isValidDateRange ? 'valid-range' : 'invalid-range']"
          range
        >
          <template #footer>
            <button @click="clearDatePicker" class="clear-button">Clear</button>
          </template>
        </date-picker>
      </app-dropdown>
    </div>
    <div v-if="filterData.type === 'numberRange'" class="filter">
      <app-dropdown
        @remove="$emit('remove')"
        @toggle="isDropdownOpen = $event"
        :label="filterData.label"
        :default-value="filterData.defaultValue"
        :selectedValue="selectedValue"
        :open="isDropdownOpen"
        hasRemoveButton
      >
        <div class="number-range">
          <div>{{ numberRangeValues | rangeFormat }}</div>
          <!-- TODO: fetch range from server based on existing donations -->
          <vue-slider v-model="numberRangeValues" :min="1" :max="10000" tooltip="none" />
          <app-primary-button
            @click="
              handleRangeSelection();
              isDropdownOpen = false;
            "
            text="Apply"
            size="sm"
            full
          />
        </div>
      </app-dropdown>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// TODO the styling is duplicated from app-dropdown, and should maybe be moved to a global file

.filter-input {
  padding: 1.5px;
  min-width: 130px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 38px;
  font-size: 13px;
  line-height: 11px;
  font-weight: 700;
  color: var(--purple-light-3);
  background: 0;
  border: 1px solid var(--gray-light-4);
  border-radius: 8px;

  &:focus-within {
    border: 1px solid var(--purple-light-3);
    outline: none;
  }

  .input {
    height: 100%;
    font-size: 13px;
    line-height: 11px;
    font-weight: 700;
    color: var(--primary-color);
    background: var(--white);
    border: 0;

    &:focus {
      outline: none;
    }
  }

  span {
    margin: 0 3px;
  }

  .label {
    margin: 0;
    padding: 0 8px;

    .default-value {
      color: var(--primary-color);
    }
  }

  .remove-button {
    margin-right: 8px;
    font-size: 18px;
    font-weight: 400;
    color: var(--primary-color);
    cursor: pointer;
  }
}

// slider
.number-range {
  padding: 12px 24px;
  color: var(--primary-color);
  font-size: 13px;
  line-height: 32px;
  font-weight: 600;

  ::v-deep .vue-slider {
    $background-light: #3747b940;

    margin: 16px 6px;
    width: 154px !important;
    height: 1px !important;
    color: var(--gray-light-2);

    .vue-slider-process {
      background: var(--primary-color);
    }

    .vue-slider-dot-handle {
      background: var(--primary-color-gradient);
      box-shadow: 0 4px 7px #001ddb40;
      // transition: 0.2s ease; // not smooth

      &:active {
        background: var(--primary-color-gradient);
        box-shadow: 0 0 0 5px $background-light;
      }
    }
  }
}

// date-picker
::v-deep .mx-datepicker {
  .mx-datepicker-main {
    padding: 14px 0 10px;
    border: 0;
    font-family: 'Nunito', Arial, sans-serif;

    .mx-btn-text {
      color: var(--primary-color);
      text-transform: uppercase;
      font-weight: 700;
    }

    .mx-calendar {
      width: 300px;
    }

    .mx-calendar-header {
      .mx-btn-icon-left,
      .mx-btn-icon-right {
        padding: 0 11px;
        border-radius: 10px;
        background-color: var(--gray-light-2);

        &:focus {
          outline: none;
        }
      }

      .mx-btn-icon-double-right,
      .mx-btn-icon-double-left {
        color: var(--purple-light-5);

        &:focus {
          outline: none;
        }
      }
    }

    .mx-datepicker-body {
      .mx-calendar-content {
        height: auto;

        table {
          thead {
            tr {
              th {
                color: var(--purple-light-5);
                visibility: hidden;

                &:first-letter {
                  visibility: visible;
                }
              }
            }
          }
        }
      }
    }

    .mx-datepicker-footer {
      padding: 14px 24px 0;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      width: 100%;
      font-family: 'Nunito', Arial, sans-serif;
      border: 0;

      .mx-datepicker-btn-confirm,
      .clear-button {
        width: 170px;
        background: 0;
        border: 0;
      }

      .clear-button {
        color: var(--primary-color);
      }

      .mx-datepicker-btn-confirm {
        font-size: 13px;
        line-height: 20px;
        color: white;
        background: var(--primary-color-gradient);
        box-shadow: 0 4px 7px #001ddb40;
        border: 0;
        border-radius: 8px;
        opacity: 0.5;
        pointer-events: none;
        transition: 0.2s ease-out;
      }
    }

    td.cell {
      position: relative;
      text-align: center;
      padding: 3px 0 4px;
      color: var(--primary-color);

      > div {
        display: inline-block;
        width: 100%;
        padding: 8px;
      }

      &.not-current-month {
        color: var(--gray-4);
      }

      &:hover {
        background: none;
        background: var(--gray-light-2);
        border-radius: 10px;
      }

      &.active {
        background: none;

        > div {
          position: relative;
          color: white;
          width: 33px;
          background: var(--primary-color-gradient);
          box-shadow: 0 4px 7px #001ddb40;
          border-radius: 10px;
          z-index: 1;
        }
      }

      &.in-range {
        background: none;

        > div {
          background: var(--gray-light-2);
        }
      }

      &.active + td.cell.active,
      &.active + td.cell.in-range {
        &::before {
          content: '';
          border-radius: 10px 0 0 10px;
          position: absolute;
          width: 50%;
          top: 2px;
          right: 100%;
          bottom: 4px;
          background: var(--gray-light-2);
          z-index: 0;
        }
      }

      &.in-range + td.cell.active,
      &.active + td.cell.active {
        &::after {
          content: '';
          border-radius: 0 10px 10px 0;
          position: absolute;
          top: 2px;
          bottom: 4px;
          left: 0;
          right: 50%;
          background: var(--gray-light-2);
          z-index: 0;
        }
      }
    }

    tr td:last-of-type.in-range > div {
      border-radius: 0 10px 10px 0;
    }

    tr td:first-of-type.in-range > div {
      border-radius: 10px 0 0 10px;
    }
  }

  &.valid-range {
    .mx-datepicker-btn-confirm {
      opacity: 1 !important;
      pointer-events: all !important;
    }
  }
}
</style>
